import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaTrashAlt,
  FaSearch,
  FaCalendarAlt,
  FaArrowLeft,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import "./ContinueAssessment.css";
import { API_BASE } from "../apiConfig";
import Toast from "./Toast";

// Pathway questions are stored as rich text, so the resume-point label has to
// be stripped down to plain text before it can go in a narrow table cell.
const stripHtml = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

// Turns the stored `last_node` into a short, human-readable resume point.
//
// `last_node` can point at either a question node or one of that question's
// options (selecting an option is what advances the draft), so both are
// resolved back to the question the therapist will land on.
const describeProgress = (pathway, lastNode) => {
  if (lastNode === null || lastNode === undefined || lastNode === "") return "Not started";
  const target = String(lastNode);
  const questions = Array.isArray(pathway) ? pathway : [];
  if (questions.length === 0) return "Not started";

  const direct = questions.find((q) => String(q.node) === target);
  if (direct) {
    const text = stripHtml(direct.question);
    return text.length > 46 ? `${text.slice(0, 46).trimEnd()}...` : text || "In progress";
  }

  const owner = questions.find((q) =>
    (Array.isArray(q.options) ? q.options : []).some((o) => String(o.node) === target)
  );
  if (owner) {
    const text = stripHtml(owner.question);
    return text.length > 46 ? `${text.slice(0, 46).trimEnd()}...` : text || "In progress";
  }

  return "In progress";
};

// Machine key -> the ?appointment= value /start-assessment uses to restore the
// right mode. Both review modes run the same `review` pathway, so the mode can
// only be recovered from the per-session label the API returns.
//
// The Initial value is sent too (it used to be an empty string, which dropped
// the param entirely): /start-assessment only narrows the step-1 radio group
// to the one matching option when ?appointment= is present, so an Initial
// draft used to resume with all three options selectable.
const APPOINTMENT_PARAM_BY_KEY = {
  initial: "Initial",
  review: "Review",
  review_smart_goal: "Review Smart Goal",
};

// Fallback for rows where the API returned no appointment_key: the display
// label ("Initial" | "Review" | "Review Smart Goal") is exactly what
// /start-assessment normalises, so it is used as-is.
const normaliseAppointmentLabel = (label) => {
  const value = String(label || "").trim().toLowerCase();
  if (value === "initial") return "Initial";
  if (value === "review") return "Review";
  if (value === "review smart goal" || value === "review_smart_goal" || value === "reviewsmartgoal" || value === "smart goal") {
    return "Review Smart Goal";
  }
  return "";
};

// The "Session" column value for a draft row.
//
// `appointmentType` is the label the API persisted on the session itself (node 1)
// and is the most accurate source, but older rows can have it blank - those resolve
// from `appointmentKey` through the same map /start-assessment uses.
//
// The last-resort "Unknown" matters: this page used to render THREE separate
// tables, each filtered on one exact `appointmentKey` literal and each with its own
// hardcoded group default. A row whose key was none of "initial" / "review" /
// "review_smart_goal" matched no table at all and silently disappeared from the
// page - it existed, was counted in the pagination total, and was never rendered.
// One table over the whole page slice cannot drop a row.
const sessionLabelFor = (row) => {
  const label =
    normaliseAppointmentLabel(row?.appointmentType) ||
    APPOINTMENT_PARAM_BY_KEY[row?.appointmentKey] ||
    String(row?.appointmentType || "").trim() ||
    "Unknown";

  // Display-only wording for the two review modes in the "Session" column.
  // The resume URL / search keep using the base labels above ("Review" and
  // "Review Smart Goal"), only what the therapist reads changes:
  //   Review              -> Review to Set New Goal
  //   Review Smart Goal   -> Review Smart Goal Progress
  if (label === "Review") return "Review to Set New Goal";
  if (label === "Review Smart Goal") return "Review Smart Goal Progress";
  return label;
};

// Build the "Continue Assessment" target for a draft row.
//
// Everything resumes on /start-assessment, which owns all three modes and
// restores the recorded node. Review drafts used to be sent to
// /review-assessment, which is the older standalone page and could not tell a
// Review Smart Goal from a plain Review - so a Review Smart Goal draft reopened
// in the wrong mode. The `appointment` param is required: without it the page
// offers all three appointments on step 1 and defaults to the Initial form.
//
// For Review Smart Goal drafts, the goalSessionId is also passed so the
// revision context is restored alongside the assessment state.
const buildResumeUrl = (row) => {
  const params = new URLSearchParams({ sessionId: String(row.sessionId) });
  const appointment =
    APPOINTMENT_PARAM_BY_KEY[row.appointmentKey] ||
    normaliseAppointmentLabel(row.appointmentType) ||
    "Initial";
  const isSmartGoalDraft = appointment === "Review Smart Goal";
  params.set("appointment", appointment);
  // Restore the goal session context for Review Smart Goal drafts.
  // Without this, resuming a Review Smart Goal draft from /continue-assessment
  // would lose the revision context and create a new goal instead of revising
  // the existing one.
  if (isSmartGoalDraft && row.goalSessionId) {
    params.set("goalSessionId", String(row.goalSessionId));
  }
  // Also pass client pre-fill for Review Smart Goal drafts
  if (isSmartGoalDraft) {
    const clientName = row.name;
    if (clientName) params.set("client", clientName);
    const ageGroup = row.ageGroup;
    if (ageGroup) params.set("age", ageGroup);
  }
  return `/start-assessment?${params.toString()}`;
};

const ContinueAssessment = () => {
  // State for data and loading
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();

  // State for filters
  const [globalSearch, setGlobalSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Refs for date inputs
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const deleteModalRef = useRef(null);
  const previousFocusRef = useRef(null);

  // 1. Fetch Data Function
  const fetchDraftSessions = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");
    const url = `${API_BASE}/therapist_api/get_draft_sessions.php`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      const result = await response.json();


      if (result.status && result.data) {
        // Map API data to table structure
        const mappedData = result.data.map((item) => {
          // For drafts, the "Assessment Date" reflects when the session was
          // last saved for later (saved_at) so review sessions don't show the
          // client's original creation date.
          const fullDateStr = item.saved_at || item.started_at || item.created_at || "";
          let displayDate = "";

          if (fullDateStr) {
            try {
              let clean = String(fullDateStr).trim();
              if (!clean.includes('T') && !clean.includes('+') && !clean.includes('Z')) {
                clean = clean.replace(' ', 'T') + '+11:00';
              }
              const d = new Date(clean);
              if (!isNaN(d.getTime())) {
                displayDate = d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
              }
            } catch(e) {}
            if (!displayDate) {
              const parts = fullDateStr.split(" ");
              const datePart = parts[0]; // "2026-05-11"
              if (datePart) {
                const [y, m, d] = datePart.split('-');
                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                displayDate = `${parseInt(d)} ${months[parseInt(m) - 1]} ${y}`;
              }
            }
          }

          return {
            id: item.client_code || "N/A",
            name: item.client_name || "Unknown",
            assessmentDate: displayDate,
            sessionId: item.sessionId, // Used for Link and Key
            lastNode: item.last_node,
            progress: describeProgress(item.pathway, item.last_node),
            // Display label straight from the API: "Initial" | "Review" |
            // "Review Smart Goal". Both review modes share the one `review`
            // pathway, so the label is persisted per session (node 1) - deriving
            // it from create_pathway made every Review Smart Goal read "Review".
            appointmentType: item.appointment_type || null,
            // Machine key: "initial" | "review" | "review_smart_goal". Drives the
            // resume route and restores the correct mode on /start-assessment.
            appointmentKey: item.appointment_key || "",
            ageGroup: item.age_group || "",
            originalDate: fullDateStr, // Used for filtering
            // get_draft_sessions.php emits the camelCase key "goalSessionId"
            // (alongside "sessionId"/"client_name"). Reading the snake_case
            // spelling here always yielded null, so resuming a Review Smart
            // Goal draft lost the goal context and opened a fresh goal instead
            // of revising the existing one. Both spellings are accepted so a
            // rename of the API field cannot silently break it again.
            goalSessionId: item.goalSessionId ?? item.goal_session_id ?? null
          };
        });

        console.log("Mapped Data to be set in State:", mappedData); // CHECK 2
        setClients(mappedData);
      } else {
        console.warn("API returned no data status");
        setError("No draft sessions found.");
        setClients([]);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchDraftSessions();
  }, []);

  const handleContinueAssessment = (id) => {
    console.log(`Continue assessment for session: ${id}`);
    // Navigation is handled by the Link component, this is just for logging or side effects if needed
  };

  // FIX: Delete by sessionId, NOT by client_code (id)
  // Update imports at the top of the file if not already present
  // import { FaTrashAlt, FaSearch, FaCalendarAlt, FaArrowLeft } from "react-icons/fa";

  // ... inside the component ...

  // Delete state
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteSessionIds, setDeleteSessionIds] = useState([]);

  // Multi-select (checkbox) state. The per-row selection checkboxes are not
  // part of the spec table, so the list stays single-select; the state is kept
  // because the bulk-delete bar is driven by it.
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);

  const handleDeleteClient = (sessionId) => {
    setDeleteSessionIds([sessionId]);
    setShowDeletePopup(true);
  };

  const handleDeleteSelected = () => {
    if (selectedSessionIds.length === 0) return;
    setDeleteSessionIds(selectedSessionIds);
    setShowDeletePopup(true);
  };

  const confirmDelete = async () => {
    setShowDeletePopup(false);
    setToast(null);
    const token = localStorage.getItem("token");
    let failed = false;
    let failureMessage = "";

    try {
      for (const id of deleteSessionIds) {
        const response = await fetch(`${API_BASE}/therapist_api/delete_response.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            sessionId: parseInt(id)
          })
        });

        const result = await response.json();
        if (!result.status) {
          failed = true;
          failureMessage = result.message || "Unknown error";
        }
      }

      if (!failed) {
        setSelectedSessionIds([]);
        await fetchDraftSessions();
        setToast({
          message:
            deleteSessionIds.length > 1
              ? "Sessions deleted successfully!"
              : "Session deleted successfully!",
          type: "success",
        });
      } else {
        setToast({
          message: `Failed to delete session(s): ${failureMessage}`,
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting session(s):", error);
      setToast({
        message: "An error occurred while deleting the session(s). Please try again.",
        type: "error",
      });
    } finally {
      setDeleteSessionIds([]);
    }
  };

  const cancelDelete = useCallback(() => {
    setShowDeletePopup(false);
    setDeleteSessionIds([]);
  }, []);

  // 3. Add this JSX just before the closing </div> of the main container 
  // (or anywhere at the bottom of the return statement, before the final <>)

  // 2. Filter Logic
  const filteredClients = clients.filter((client) => {
    // Global Search - matches ANY column (safe for numbers/null)
    const q = globalSearch.toLowerCase();
    const matchesGlobal =
      String(client.id ?? "").toLowerCase().includes(q) ||
      String(client.name ?? "").toLowerCase().includes(q) ||
      String(client.ageGroup ?? "").toLowerCase().includes(q) ||
      String(client.appointmentType ?? "").toLowerCase().includes(q) ||
      String(client.assessmentDate ?? "").toLowerCase().includes(q) ||
      String(client.progress ?? "").toLowerCase().includes(q);

    // Date Range
    let clientDateObj = null;
    if (client.originalDate) {
      clientDateObj = new Date(client.originalDate);
    }

    const from = fromDate ? new Date(fromDate) : new Date("1900-01-01");
    const to = toDate ? new Date(toDate) : new Date("2100-12-31");

    const matchesDateRange = clientDateObj
      ? (clientDateObj >= from && clientDateObj <= to)
      : true;

    return (
      matchesGlobal &&
      matchesDateRange
    );
  });

  // Sort: latest date first
  const sortedClients = [...filteredClients].sort(
    (a, b) => new Date(b.originalDate) - new Date(a.originalDate)
  );

  // Pagination Logic
  const totalPages = Math.ceil(sortedClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [sortedClients.length, totalPages, currentPage]);

  const currentClients = sortedClients.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Focus trap for delete modal
  useEffect(() => {
    if (showDeletePopup) {
      previousFocusRef.current = document.activeElement;
      const timer = setTimeout(() => {
        const modal = deleteModalRef.current;
        if (modal) {
          const focusable = modal.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length > 0) focusable[0].focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [showDeletePopup]);

  const handleModalKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      cancelDelete();
      return;
    }
    if (e.key === "Tab" && deleteModalRef.current) {
      const focusable = deleteModalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [cancelDelete]);

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <Link onClick={() => navigate(-1)} className="back-btn2" aria-label="Go back to dashboard">
        <FaArrowLeft className="back-icon" aria-hidden="true" />
        Previous page
      </Link>
      <div className="continue-container">
        <div className="common-page-header">
          <h1 className="common-page-title">Continue Assessment</h1>
          <p className="common-page-subtitle">Continue a paused diagnosis of your client</p>
        </div>

        {/* Search and Filter */}
        <div className="filter-section">
          <div className="search-box">
            <div className="common-search-box">
              <input
                type="text"
                placeholder="Search"
                value={globalSearch}
                onChange={(e) => {
                  setGlobalSearch(e.target.value);
                  setCurrentPage(1);
                }}
                aria-label="Search clients by ID, name, age group, session type, or date"
              />
              <FaSearch className="common-search-icon" aria-hidden="true" />
            </div>
          </div>

          {/* From Date */}
          <div className="filter-date-range">
            <div className="filter-date-wrapper">
              <label htmlFor="from-date" className="sr-only">From Date</label>
              <div className="filter-date-box" role="button" tabIndex="0" onClick={() => fromRef.current?.showPicker()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fromRef.current?.showPicker(); } }} aria-label={`Filter from date${fromDate ? `: ${fromDate}` : ""}`} aria-haspopup="dialog">
                <span className={!fromDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {fromDate || "From"}
                </span>
                <FaCalendarAlt className="filter-date-icon" aria-hidden="true" />
                <input
                  type="date"
                  id="from-date"
                  name="fromDate"
                  ref={fromRef}
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-date-input"
                  aria-label="Filter from date"
                />
              </div>
            </div>

            {/* To Date */}
            <div className="filter-date-wrapper">
              <label htmlFor="to-date" className="sr-only">To Date</label>
              <div className="filter-date-box" role="button" tabIndex="0" onClick={() => toRef.current?.showPicker()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toRef.current?.showPicker(); } }} aria-label={`Filter to date${toDate ? `: ${toDate}` : ""}`} aria-haspopup="dialog">
                <span className={!toDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {toDate || "To"}
                </span>
                <FaCalendarAlt className="filter-date-icon" aria-hidden="true" />
                <input
                  type="date"
                  id="to-date"
                  name="toDate"
                  ref={toRef}
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="filter-date-input"
                  aria-label="Filter to date"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bulk delete toolbar */}
        {selectedSessionIds.length > 0 && (
          <div className="bulk-delete-bar" role="status" aria-live="polite">
            <span className="bulk-delete-count">
              {selectedSessionIds.length} session{selectedSessionIds.length > 1 ? "s" : ""} selected
            </span>
            <button className="bulk-delete-btn" onClick={handleDeleteSelected} aria-label={`Delete ${selectedSessionIds.length} selected session${selectedSessionIds.length > 1 ? "s" : ""}`}>
              <FaTrashAlt aria-hidden="true" /> Delete Selected
            </button>
          </div>
        )}

        {loading ? (
          <div role="status" aria-live="polite" style={{ textAlign: "center", padding: "20px" }}>Loading sessions...</div>
        ) : error ? (
          <div role="alert" style={{ textAlign: "center", color: "var(--cpa-error-red, #C22B27)", padding: "20px" }}>{error}</div>
        ) : (
          <>
            {/*
                ONE table for all three session kinds - Initial, Review and
                Review Smart Goal.

                These were three separate tables, each filtered on one exact
                `appointmentKey` literal and each with its own hardcoded heading and
                row-group default. Three problems with that:

                  1. A row whose key matched none of the three was rendered by NO
                     table and vanished, while still being counted by the pagination
                     total (which is computed from `sortedClients`, not from the
                     three filtered groups).
                  2. Sections appeared and disappeared as data changed, so the page
                     layout jumped around.
                  3. The same 6 columns were repeated three times, so any column
                     change had to be made in three places - which is how
                     "Appointment Type" came to be out of step with the "Session"
                     heading the Review Existing Goals and Client History pages
                     already use for the identical column.

                The rows are already sorted newest-first by `sortedClients` and
                already sliced to the current page by `currentClients`, so one table
                over `currentClients` keeps search, date filtering and pagination
                working exactly as before.
            */}
            {currentClients.length === 0 ? (
              <div role="status" style={{ textAlign: "center", padding: "20px" }}>
                No sessions found.
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="client-table" aria-label="Assessment Sessions">
                  <thead>
                    <tr>
                      <th scope="col">Client ID</th>
                      <th scope="col">Client Name</th>
                      <th scope="col">Age Group</th>
                      <th scope="col">Session</th>
                      <th scope="col">Assessment Date</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentClients.map((c, index) => (
                      <tr key={c.sessionId || index} className={index % 2 === 1 ? "alt-row" : ""}>
                        <td style={{ textAlign: 'left' }}>{c.id}</td>
                        <td style={{ textAlign: 'left' }}>{c.name}</td>
                        <td>{c.ageGroup}</td>
                        <td>{sessionLabelFor(c)}</td>
                        <td>{c.assessmentDate}</td>
                        <td style={{ textAlign: "center" }}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <Link
                              to={buildResumeUrl(c)}
                              className="continue-btn1"
                              onClick={() => handleContinueAssessment(c.sessionId)}
                              aria-label={`Continue assessment for ${c.name}`}
                            >
                              <span className="continue-text">Continue Assessment</span>
                            </Link>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteClient(c.sessionId)}
                              aria-label={`Delete session for ${c.name}`}
                            >
                              <FaTrashAlt aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {sortedClients.length > itemsPerPage && (
              <nav className="pagination" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Go to previous page"
                >
                  Prev
                </button>

                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageChange(i + 1)}
                    className={currentPage === i + 1 ? "active" : ""}
                    aria-label={`Go to page ${i + 1}`}
                    aria-current={currentPage === i + 1 ? "page" : undefined}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Go to next page"
                >
                  Next
                </button>
              </nav>
            )}
          </>
        )}
      </div>
      {showDeletePopup && (
        <div
          className="delete-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
          aria-describedby="delete-modal-desc"
          ref={deleteModalRef}
          onKeyDown={handleModalKeyDown}
        >
          <div className="delete-modal-content">
            <h3 id="delete-modal-title">Delete Session{deleteSessionIds.length > 1 ? "s" : ""}</h3>
            <p id="delete-modal-desc">
              Are you sure you want to delete {deleteSessionIds.length} session{deleteSessionIds.length > 1 ? "s" : ""}?
            </p>
            <div className="delete-modal-actions">
              <button
                onClick={cancelDelete}
                aria-label="Cancel delete"
                style={{
                  padding: '10px 20px',
                  border: '1px solid var(--cpa-gray-3, #737A7E)',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                No
              </button>
              <button
                onClick={confirmDelete}
                aria-label={`Confirm delete ${deleteSessionIds.length} session${deleteSessionIds.length > 1 ? "s" : ""}`}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  backgroundColor: 'var(--cpa-error-red, #C22B27)',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContinueAssessment;
